import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Col,
} from "reactstrap";
import { Link } from "react-router-dom";
import NotificationAlert from "react-notification-alert";

import { Form } from "@unform/web";
import * as Yup from "yup";
import logo from "../../../assets/img/autopilot-logo.png";
import getValidationErrors from "../../../utils/getValidationErrors";
import CustomInputGroupText from "components/CustomInputGroupText";
import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import { getRegistrationStatus, resendActivation } from "services/registrationApi";
import "./login.css";

const Login = () => {
  const formRef = useRef(null);
  const alertRef = useRef();
  const { login } = useAuth();
  const { addToast } = useToast();
  const [allowRegister, setAllowRegister] = useState(false);
  // PO1-2465: quando o login retorna EMAIL_CONFIRMATION_REQUIRED, guardamos a
  // credencial (já validada no backend) p/ oferecer o reenvio de ativação.
  const [pendingActivation, setPendingActivation] = useState(null);
  const [resending, setResending] = useState(false);

  // Consulta o status público de auto-registro pra decidir se mostra o link
  // "Cadastre-se" embaixo. Falha silenciosa esconde o link.
  useEffect(() => {
    let cancelled = false;
    getRegistrationStatus()
      .then((s) => {
        if (cancelled) return;
        setAllowRegister(!!s.allowSelfRegistration);
      })
      .catch(() => {
        if (cancelled) return;
        setAllowRegister(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current.setErrors({});
        const schema = Yup.object().shape({
          username: Yup.string().required("Username é obrigatório"),
          password: Yup.string().required("Senha é obrigatória"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });
        await login(data);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current.setErrors(errors);
          return;
        }
        // PO1-2465: e-mail não confirmado — a credencial já foi validada no
        // backend (userId veio no payload). Oferece o reenvio de ativação.
        const payload = error && error.response && error.response.data;
        if (payload && payload.code === "EMAIL_CONFIRMATION_REQUIRED") {
          setPendingActivation({
            username: data.username,
            password: data.password,
          });
          addToast({
            place: "tr",
            type: "warning",
            title: "Confirme seu e-mail",
            description:
              "Sua conta ainda não teve o e-mail confirmado. Verifique sua caixa postal ou reenvie o e-mail de ativação abaixo.",
            icon: "tim-icons icon-email-85",
          });
          return;
        }
        setPendingActivation(null);
        const message = {
          place: "tr",
          type: "danger",
          title: "Error",
          description:
            "Autenticação não autorizada, verifique suas credenciais!",
          icon: "tim-icons icon-alert-circle-exc",
        };
        addToast(message);
      }
    },
    [addToast, login]
  );

  // PO1-2465: reenvia o e-mail de ativação reusando a credencial já digitada.
  const handleResendActivation = useCallback(async () => {
    if (!pendingActivation || resending) return;
    setResending(true);
    try {
      await resendActivation(pendingActivation);
    } catch (e) {
      // Resposta do backend é neutra; não há erro a exibir.
    }
    setResending(false);
    setPendingActivation(null);
    addToast({
      place: "tr",
      type: "success",
      title: "E-mail de ativação",
      description:
        "Se a conta existir e ainda não estiver confirmada, reenviamos o e-mail de ativação. Verifique sua caixa postal.",
      icon: "tim-icons icon-email-85",
    });
  }, [addToast, pendingActivation, resending]);

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={alertRef} />
      </div>
      <div className="content">
        <Container>
          <Col className="ml-auto mr-auto" lg="4" md="6">
            <Form ref={formRef} onSubmit={handleSubmit}>
              <Card className="glass-card">
                <CardHeader>
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255, 255, 255, 0.92)",
                    borderRadius: "16px",
                    width: "160px",
                    height: "80px",
                    marginBottom: "16px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                  }}>
                    <img
                      alt="Everlog"
                      src={logo}
                      style={{ width: "140px", objectFit: "contain" }}
                    />
                  </div>
                  <h4>
                    Bem-vindo à <strong>Everlog</strong>
                  </h4>
                </CardHeader>
                <CardBody>
                  <CustomInputGroupText
                    name="username"
                    placeholder="Usuário"
                    type="text"
                    addonType="prepend"
                    icon="tim-icons icon-single-02"
                  />
                  <CustomInputGroupText
                    name="password"
                    type="password"
                    placeholder="Senha"
                    addonType="prepend"
                    icon="tim-icons icon-lock-circle"
                  />
                </CardBody>
                <CardFooter>
                  <Button
                    block
                    className="btn-entrar mb-3"
                    size="lg"
                    type="submit"
                  >
                    Entrar
                  </Button>
                  <div style={{ textAlign: "center" }}>
                    <Link
                      to="/auth/forgot-password"
                      style={{ color: "#fff", fontSize: 13 }}
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  {pendingActivation && (
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                      <Button
                        color="link"
                        size="sm"
                        type="button"
                        disabled={resending}
                        onClick={handleResendActivation}
                        style={{ color: "#ffd27f", fontSize: 13 }}
                      >
                        {resending
                          ? "Reenviando..."
                          : "Reenviar e-mail de ativação"}
                      </Button>
                    </div>
                  )}
                  {allowRegister && (
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                      <Link
                        to="/auth/register"
                        style={{ color: "#fff", fontSize: 13 }}
                      >
                        Não tem conta? Cadastre-se
                      </Link>
                    </div>
                  )}
                </CardFooter>
              </Card>
            </Form>
          </Col>
        </Container>
      </div>
    </>
  );
};

export default Login;
